import { AiPipeline, AiPortfolio } from '../types';

/**
 * AI portfolio projects are loaded dynamically at runtime — no project text
 * lives in the codebase. Each project is a folder under
 * public/portfolios/ai-portfolio/<slug>/ holding a .json (metadata), a .md
 * (case study), and a .svg (thumbnail). A build step scans those folders and
 * writes manifest.json; this loader reads it and the per-project JSON.
 *
 * To add a project: drop the folder in and restart `npm run dev` (or rebuild).
 */

const MANIFEST_URL = '/portfolios/ai-portfolio/manifest.json';

interface ManifestEntry {
  slug: string;
  json: string;
  markdown: string | null;
  image: string | null;
  pipelines?: AiPipeline[];
}

// The metadata JSON schema is intentionally loose so a pasted summarized.json
// works as-is. Only a handful of fields are read.
interface ProjectMeta {
  title?: string;
  tagline?: string;
  status?: string;
  tags?: string[];
  tools?: string[];
  repo_url?: string;
  live_url?: string;
}

export async function fetchAiPortfolios(): Promise<AiPortfolio[]> {
  let entries: ManifestEntry[];
  try {
    const res = await fetch(MANIFEST_URL, { cache: 'no-cache' });
    if (!res.ok) return [];
    entries = await res.json();
  } catch {
    return [];
  }

  const projects = await Promise.all(
    entries.map(async (entry): Promise<AiPortfolio | null> => {
      try {
        const res = await fetch(entry.json, { cache: 'no-cache' });
        if (!res.ok) return null;
        const meta: ProjectMeta = await res.json();

        const tags =
          meta.tags && meta.tags.length ? meta.tags : meta.tools ?? [];

        return {
          slug: entry.slug,
          title: meta.title ?? entry.slug,
          tagline: meta.tagline ?? '',
          status: meta.status || undefined,
          tags,
          image: entry.image ?? undefined,
          repoUrl: meta.repo_url || undefined,
          liveUrl: meta.live_url || undefined,
          markdown: entry.markdown ?? undefined,
          pipelines: entry.pipelines ?? [],
        };
      } catch {
        return null;
      }
    }),
  );

  return projects.filter((p): p is AiPortfolio => p !== null);
}
